/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-04-06 08:57:41
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React from 'react';
import { TextStyle } from '@shopify/polaris';
import { PinMajor, ArrowUpMinor, ArrowDownMinor, HideMinor } from '@shopify/polaris-icons';
import {
  FilterIcon,
  FilterIconDisable,
  BannerIconDisable
} from 'src/components/containers/common/icon';
import { RankingIcon } from 'src/components/containers/common/icon/rankingIcon';
import Lottie from 'react-lottie';
import moment from 'moment';

import dateRangeGif from 'src/assets/json/merchandising/gif-date-range.json';
import consequenceGif from 'src/assets/json/merchandising/gif-consequences.json';
import conditionGif from 'src/assets/json/merchandising/gif-conditions.json';

export const MAX_FILE_SIZE = 2000000;
export const ACCEPT_FILE_IMAGE_TYPE = 'image/png, image/jpg, image/jpeg';

// Display 10 merchandising rules per page
export const NUMBER_MERCHANDISING_RULES_PER_PAGE = 10;
export const NUMBER_MERCHANDISING_RULES = 20;
export const NUMBER_PRODUCT_PER_PAGE = 30;
export const NUMBER_ATTRIBUTES_PER_CONSEQUENCE = 3;
export const NUMBER_ATTRIBUTE_PER_RULE = {
  boost: 3,
  demote: 3,
  hide: 3,
  filter: 3,
  banner: 1
};

export const STRATEGY_FOR_TAB_PRODUCT = ['boost', 'demote', 'hide', 'filter'];
export const NUMBER_CONDITION_PER_TYPE = {
  boost: 3,
  demote: 3,
  hide: 7,
  filter: 3
};
export const PIN_LIMIT = 7;

export const PREVIEW_PRODUCT_PER_ROW = 4;
export const PREVIEW_PRODUCT_PER_PAGE = 12;

export const STATUS_CHOICES = [
  { label: 'AdminHTML.MerchandisingV2.Constant.status1_Label', value: 'enabled' },
  { label: 'AdminHTML.MerchandisingV2.Constant.status2_Label', value: 'disabled' }
];

export const STRATEGY_FOR_ALL_QUERY = ['boost', 'demote', 'hide'];
export const STRATEGY_FOR_QUERY = [
  'pin',
  'boost',
  'demote',
  'hide',
  'filter',
  'banner',
  'productPerformanceRanking'
];
export const STRATEGY_FOR_ALL_COLLECT = ['boost', 'demote', 'hide'];
export const STRATEGY_FOR_COLLECT = ['pin', 'boost', 'demote', 'hide', 'banner'];

export const COLUMN_COMMERCE_OPTIONS = [
  { label: 'AdminHTML.MerchandisingV2.Constant.column1_Label', value: 'status', default: true },
  { label: 'AdminHTML.MerchandisingV2.Constant.column2_Label', value: 'queries', default: true },
  { label: 'AdminHTML.MerchandisingV2.Constant.column4_Label', value: 'strategies', default: true },
  { label: 'AdminHTML.MerchandisingV2.Constant.column5_Label', value: 'dateRange', default: true },
  { label: 'AdminHTML.MerchandisingV2.Constant.column6_Label', value: 'actions', default: true }
];

export const COLUMN_COLLECTION_OPTIONS = [
  { label: 'AdminHTML.MerchandisingV2.Constant.column1_Label', value: 'status', default: true },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.column3_Label',
    value: 'collections',
    default: true
  },
  { label: 'AdminHTML.MerchandisingV2.Constant.column4_Label', value: 'strategies', default: true },
  { label: 'AdminHTML.MerchandisingV2.Constant.column5_Label', value: 'dateRange', default: true },
  { label: 'AdminHTML.MerchandisingV2.Constant.column6_Label', value: 'actions', default: true }
];

const renderAttributesDisplay = (attributes, optionList = [], i18n) => {
  return _.map(attributes, (item, index) => {
    const { attribute, conductor, label, title, value, id } = item;

    let formatAttribute = attribute;
    let valueDisplay = value;

    if (attribute === 'available') {
      formatAttribute = 'inventory';
    }

    switch (attribute) {
      case 'product_id':
        valueDisplay = title ? title : id;
        break;
      case 'available':
        formatAttribute = 'inventory';
        break;
      case 'collection_id':
        valueDisplay = label ? label : value;
        formatAttribute = 'collection';
        break;
      case 'product_option':
        formatAttribute = 'option';
        break;
      case 'created_at':
      case 'published_at': {
        valueDisplay = value ? moment(value).format('YYYY-MM-DD') : '';
        break;
      }
      default:
        formatAttribute = attribute;
    }
    const attributeObject = _.find(PRODUCT_ATTRIBUTES, item => item.value === formatAttribute);

    const attributeLabel = _.isEmpty(attributeObject)
      ? i18n.translate('AdminHTML.MerchandisingV2.Constant.productAttribute_Label', {
          nt_option: 'product'
        })
      : i18n.translate(attributeObject.label);

    const conductorList = CONDUCTOR_FOR_ATTRIBUTES[formatAttribute];

    const conductorObject = _.isEmpty(conductorList)
      ? {}
      : _.find(conductorList, item => item.value === conductor);
    const conductorLabel = _.isEmpty(conductorObject)
      ? i18n.translate('AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label')
      : i18n.translate(conductorObject.label);

    let attributeLabelOption = attributeLabel;

    const newOptionList = optionList;
    // same with case attribute = opt_
    if (_.includes(attributeLabel, 'Option')) {
      const arrayOpt = valueDisplay.split(':');
      const option = _.find(newOptionList, { name: arrayOpt[0] });
      attributeLabelOption = _.get(option, 'originalName', attributeLabel);
      valueDisplay = arrayOpt[1];
    }
    const attributeDisplay = i18n.translate(
      'AdminHTML.MerchandisingV2.ListRules.consequence_Attribute',
      {
        nt_attribute: attributeLabelOption,
        nt_conductor: conductorLabel,
        nt_value: valueDisplay || ''
      }
    );
    return (
      <span key={index}>
        {index === 0 ? (
          <TextStyle variation="strong">{attributeDisplay}</TextStyle>
        ) : (
          <span>
            {` ${i18n.translate('AdminHTML.MerchandisingV2.Constant.condition_And')} `}
            <TextStyle variation="strong">{attributeDisplay}</TextStyle>
          </span>
        )}
      </span>
    );
  });
};

const filterConsequences = consequences => {
  return _.filter(consequences, consequence => !_.isEmpty(consequence.attributes));
};

export const CONSEQUENCE_TYPES = {
  pin: {
    sourceIcon: PinMajor,
    renderTooltip: (consequences: Array<Object>, i18n: Object) => {
      return (
        <div>
          {_.map(consequences, (consequence, index) => {
            const { pinPositions = [] } = consequence;

            // Remove empty pin position
            const pinPositionsList = _.filter(pinPositions, item => {
              return item.id;
            });

            return (
              <div key={index}>
                {_.map(pinPositionsList, (item, itemIndex) => (
                  <p key={itemIndex}>
                    {i18n.translate('AdminHTML.MerchandisingV2.ListRules.consequence_Pin', {
                      nt_product: (
                        <TextStyle variation="strong">
                          {item.title ? item.title : item.id}
                        </TextStyle>
                      ),
                      nt_position: i18n.ordinal(item.position + 1)
                    })}
                  </p>
                ))}
              </div>
            );
          })}
        </div>
      );
    }
  },
  banner: {
    sourceIcon: BannerIconDisable,
    renderTooltip: (consequences: Array<Object>, i18n: Object, optionList: Array<Object>) => {
      return (
        <div>
          {_.map(consequences, (consequence, index) => {
            const { name, position, imgSrc } = consequence;
            if (!name || (!position && position !== 0) || !imgSrc) {
              return null;
            }
            return (
              <div className="consequence-detail" key={`consequence-detail-${index}`}>
                <div className="consequence-detail-content">
                  <p>
                    {i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Banner_Content', {
                      name: <TextStyle variation="strong">{name}</TextStyle>,
                      position: (
                        <TextStyle variation="strong">
                          {' '}
                          {i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Banner_Content_t', {
                            number: position + 1
                          })}
                        </TextStyle>
                      )
                    })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  },
  boost: {
    sourceIcon: ArrowUpMinor,
    renderTooltip: (consequences: Array<Object>, i18n: Object, optionList: Array<Object>) => {
      return (
        <div>
          {_.map(filterConsequences(consequences), (consequence, index) => {
            const { attributes } = consequence;
            return (
              <p key={index}>
                {i18n.translate('AdminHTML.MerchandisingV2.ListRules.consequence_Boost', {
                  nt_value: renderAttributesDisplay(attributes, optionList, i18n)
                })}
              </p>
            );
          })}
        </div>
      );
    }
  },
  demote: {
    sourceIcon: ArrowDownMinor,
    renderTooltip: (consequences: Array<Object>, i18n: Object, optionList: Array<Object>) => {
      return (
        <div>
          {/* The same with Boost, should combine to 1 function render later */}
          {_.map(filterConsequences(consequences), (consequence, index) => {
            const { attributes } = consequence;

            return (
              <p key={index}>
                {i18n.translate('AdminHTML.MerchandisingV2.ListRules.consequence_Demote', {
                  nt_value: renderAttributesDisplay(attributes, optionList, i18n)
                })}
              </p>
            );
          })}
        </div>
      );
    }
  },
  hide: {
    sourceIcon: HideMinor,
    renderTooltip: (consequences: Array<Object>, i18n: Object, optionList: Array<Object>) => {
      return (
        <div>
          {_.map(filterConsequences(consequences), (consequence, index) => {
            const { attributes } = consequence;

            return (
              <p key={index}>
                {i18n.translate('AdminHTML.MerchandisingV2.ListRules.consequence_Hide', {
                  nt_value: renderAttributesDisplay(attributes, optionList, i18n)
                })}
              </p>
            );
          })}
        </div>
      );
    }
  },
  filter: {
    sourceIcon: () => FilterIcon({ size: '0 0 20 20' }),
    sourceIconDisable: () => FilterIconDisable({ size: '0 0 20 20' }),
    renderTooltip: (consequences: Array<Object>, i18n: Object, optionList: Array<Object>) => {
      return (
        <div>
          {_.map(filterConsequences(consequences), (consequence, index) => {
            const { attributes } = consequence;

            return (
              <p key={index}>
                {i18n.translate('AdminHTML.MerchandisingV2.ListRules.consequence_Filter', {
                  nt_value: renderAttributesDisplay(attributes, optionList, i18n)
                })}
              </p>
            );
          })}
        </div>
      );
    }
  },
  productPerformanceRanking: {
    sourceIcon: () => RankingIcon(),
    renderTooltip: (queries: Array<string>, i18n: Object) => {
      return (
        <div>
          <p key="product-performance-ranking-summary">
            {queries.map((query, index) => (
              <p>
                {i18n.translate(
                  'AdminHTML.MerchandisingV2.Rule.card3_ProductPerformanceRanking_Content',
                  {
                    q: <TextStyle variation="strong">{query}</TextStyle>
                  }
                )}
              </p>
            ))}
          </p>
        </div>
      );
    }
  }
};

// export const CONDITION_SEARCH_OPERATORS = [
//   { label: 'AdminHTML.MerchandisingV2.Constant.conditionSearchOperator1_Label', value: 'contains' },
//   {
//     label: 'AdminHTML.MerchandisingV2.Constant.conditionSearchOperator2_Label',
//     value: 'not_contains'
//   },
//   {
//     label: 'AdminHTML.MerchandisingV2.Constant.conditionSearchOperator3_Label',
//     value: 'starts_with'
//   },
//   { label: 'AdminHTML.MerchandisingV2.Constant.conditionSearchOperator4_Label', value: 'ends_with' }
// ];

export const CONDITION_SEARCH_OPTION = [
  {
    label: 'AdminHTML.MerchandisingV2.Constant.conditionSearchOption2_Label',
    value: 'is'
  },
  { label: 'AdminHTML.MerchandisingV2.Constant.conditionSearchOption1_Label', value: 'contains' },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.conditionSearchOption3_Label',
    value: 'starts_with'
  },
  { label: 'AdminHTML.MerchandisingV2.Constant.conditionSearchOption4_Label', value: 'ends_with' }
];

export const CONDITION_FILTER_OPTIONS = [
  {
    label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption1_Label',
    value: 'product_type'
  },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption2_Label',
    value: 'vendor'
  },
  { label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption3_Label', value: 'price' },
  // { label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption4_Label', value: 'location' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption5_Label', value: 'tag' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption6_Label', value: 'collection' },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption8_Label',
    value: 'variant_price'
  },
  // { label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption9_Label', value: 'percent_sale' },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption10_Label',
    value: 'rating'
  },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption11_Label',
    value: 'stock_status'
  }
  // { label: 'AdminHTML.MerchandisingV2.Constant.conditionFilterOption12_Label', value: 'metafields' }
];

export const PRODUCT_ATTRIBUTES = [
  {
    label: 'AdminHTML.MerchandisingV2.Constant.productAttribute1_Label',
    value: 'title',
    nonDuplicate: true
  },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.productAttribute2_Label',
    value: 'product_type',
    nonDuplicate: true
  },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.productAttribute11_Label',
    value: 'product_category',
    nonDuplicate: true
  },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.productAttribute3_Label',
    value: 'vendor',
    nonDuplicate: true
  },
  { label: 'AdminHTML.MerchandisingV2.Constant.productAttribute4_Label', value: 'price' },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.productAttribute5_Label',
    value: 'inventory',
    nonDuplicate: true
  },
  { label: 'AdminHTML.MerchandisingV2.Constant.productAttribute6_Label', value: 'tag' },
  { label: 'AdminHTML.MerchandisingV2.Constant.productAttribute7_Label', value: 'created_at' },
  { label: 'AdminHTML.MerchandisingV2.Constant.productAttribute8_Label', value: 'published_at' },
  { label: 'AdminHTML.MerchandisingV2.Constant.productAttribute9_Label', value: 'collection' },
  {
    label: 'AdminHTML.MerchandisingV2.Constant.productAttribute10_Label',
    value: 'product_id',
    nonDuplicate: true
  }
];

export const CONDUCTOR_FOR_PRODUCT_TITLE = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label1', value: 'equal' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label3', value: 'starts_with' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label4', value: 'ends_with' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label5', value: 'contains' }
];

export const CONDUCTOR_FOR_PRODUCT_TYPE = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label1', value: 'equal' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label3', value: 'starts_with' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label4', value: 'ends_with' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label5', value: 'contains' }
];

export const CONDUCTOR_FOR_PRODUCT_VENDOR = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label1', value: 'equal' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label3', value: 'starts_with' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label4', value: 'ends_with' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label5', value: 'contains' }
];

export const CONDUCTOR_IS = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label', value: 'is' }
];

export const CONDUCTOR_FOR_PRODUCT_PRICE = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label14', value: 'equal' },

  {
    label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label7',
    value: 'greater_than'
  },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label8', value: 'less_than' }
];

export const VALUES_FOR_PRODUCT_INVENTORY = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label10', value: 'in-stock' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label11', value: 'out-of-stock' }
];

export const CONDUCTOR_FOR_PRODUCT_INVENTORY = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label10', value: 'in_stock' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label11', value: 'out_stock' }
];

export const CONDUCTOR_FOR_PRODUCT_TAG = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label1', value: 'equal' }
];

export const CONDUCTOR_FOR_DATE = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label12', value: 'before' },
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label13', value: 'after' }
];

export const CONDUCTOR_FOR_COLLECTION = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label1', value: 'equal' }
];

export const CONDUCTOR_FOR_PRODUCT_ID = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label1', value: 'equal' }
];

export const CONDUCTOR_FOR_OPTION = [
  { label: 'AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label1', value: 'equal' }
];

export const CONDUCTOR_FOR_ATTRIBUTES = {
  title: null,
  product_type: null,
  product_category: null,
  vendor: null,
  price: CONDUCTOR_FOR_PRODUCT_PRICE,
  inventory: null,
  tag: null,
  created_at: CONDUCTOR_FOR_DATE,
  published_at: CONDUCTOR_FOR_DATE,
  collection: null,
  collection_id: null,
  product_id: null,
  option: null
};
// -----------------------------------------------------------------------------------------------//

// Hide collections if there are many
// Show button More/Less to view all
export const NUMBER_COLLECTION_DISPLAY = 7;

// Rule ranking
export const RANKING = [
  { label: 'AdminHTML.MerchandisingV2.Constant.ranking1_Label', value: 'high' },
  { label: 'AdminHTML.MerchandisingV2.Constant.ranking2_Label', value: 'medium' },
  { label: 'AdminHTML.MerchandisingV2.Constant.ranking3_Label', value: 'low' }
];

// Products must match
export const PRODUCTS_MATCH = [
  { label: 'AdminHTML.MerchandisingV2.Constant.productsMatch1_Label', value: false },
  { label: 'AdminHTML.MerchandisingV2.Constant.productsMatch2_Label', value: true }
];

// Product tour config for rule page
const defaultOptions = gif => ({
  loop: true,
  autoplay: true,
  animationData: gif
});

export const CONFIG_RULE_TOUR = (i18n: Object) => {
  return [
    {
      selector: '[data-tut="reactour__date"]',
      content: () => (
        <div>
          <h2 className="heading-tour">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.Product_Tour.step1_title')}
          </h2>
          <div style={{ backgroundColor: '#F1F2F3' }}>
            <Lottie options={defaultOptions(dateRangeGif)} width={400} height={200} />
          </div>
          <div className="content-tour">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.Product_Tour.step1_content')}
          </div>
        </div>
      ),
      position: 'top',
      style: {
        backgroundColor: 'white',
        padding: 0,
        width: '400px',
        maxWidth: 'unset',
        color: 'black'
      }
    },
    {
      selector: '[data-tut="reactour__condition"]',
      content: () => (
        <div>
          <h2 className="heading-tour">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.Product_Tour.step2_title')}
          </h2>
          <div style={{ backgroundColor: '#F1F2F3' }}>
            <Lottie options={defaultOptions(conditionGif)} width={400} height={200} />
          </div>
          <div className="content-tour">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.Product_Tour.step2_content')}
          </div>
        </div>
      ),
      position: 'top',
      style: {
        backgroundColor: 'white',
        padding: 0,
        maxWidth: 'unset',
        width: '420px',
        color: 'black'
      }
    },
    {
      selector: '[data-tut="reactour__consequence"]',
      content: () => (
        <div>
          <h2 className="heading-tour">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.Product_Tour.step3_title')}
          </h2>
          <div style={{ backgroundColor: '#F1F2F3' }}>
            <Lottie options={defaultOptions(consequenceGif)} width={400} height={200} />
          </div>
          <div className="content-tour">
            {i18n.translate('AdminHTML.MerchandisingV2.Rule.Product_Tour.step3_content')}
          </div>
        </div>
      ),
      position: 'top',
      style: {
        backgroundColor: 'white',
        padding: 0,
        maxWidth: 'unset',
        width: '400px',
        color: 'black'
      }
    }
  ];
};

// Product tour config for list rule page

export const CONFIG_LIST_PAGE_TOUR = (i18n: Object) => {
  return [
    {
      selector: '[data-tut="ruleName"]',
      content: () => (
        <div>
          <h2 className="heading-tour heading_tour_list">
            {i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step01_title')}
          </h2>
          <div className="content-tour">
            <p>{i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step01_content01')}</p>
            <p>{i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step01_content02')}</p>
          </div>
        </div>
      ),
      position: 'top',
      style: {
        backgroundColor: 'white',
        padding: 0,
        width: '480px',
        maxWidth: 'unset',
        color: 'black'
      }
    },
    {
      selector: '[data-tut="conditions"]',
      content: () => (
        <div>
          <h2 className="heading-tour heading_tour_list">
            {i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step02_title')}
          </h2>
          <div className="content-tour">
            <p>{i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step02_content01')}</p>
            <p>{i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step02_content02')}</p>
          </div>
        </div>
      ),
      position: 'top',
      style: {
        backgroundColor: 'white',
        padding: 0,
        width: '480px',
        maxWidth: 'unset',
        color: 'black'
      }
    },
    {
      selector: '[data-tut="consequences"]',
      content: () => (
        <div>
          <h2 className="heading-tour heading_tour_list">
            {i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step03_title')}
          </h2>
          <div className="content-tour">
            {i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step03_content')}
          </div>
        </div>
      ),
      position: 'top',
      style: {
        backgroundColor: 'white',
        padding: 0,
        width: '480px',
        maxWidth: 'unset',
        color: 'black'
      }
    },
    {
      selector: '[data-tut="lastUpdate"]',
      content: () => (
        <div>
          <h2 className="heading-tour heading_tour_list">
            {i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step04_title')}
          </h2>
          <div className="content-tour">
            <p>{i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step04_content01')}</p>
            <p>{i18n.translate('AdminHTML.MerchandisingV2.ProductTour.step04_content02')}</p>
          </div>
        </div>
      ),
      position: 'top',
      style: {
        backgroundColor: 'white',
        padding: 0,
        width: '480px',
        maxWidth: 'unset',
        color: 'black'
      }
    }
  ];
};
